import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControlLabel,
    Checkbox,
    Typography,
    Box,
    alpha,
    useTheme,
    Chip,
    Alert,
    Autocomplete,
    TextField,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';

interface TrackEventSchoolSelectorProps {
    open: boolean;
    schools: string[];
    organizationName: string;
    onConfirm: (selectedSchools: string[]) => void;
    onCancel: () => void;
}

const TrackEventSchoolSelector: React.FC<TrackEventSchoolSelectorProps> = ({
    open,
    schools,
    organizationName,
    onConfirm,
    onCancel,
}) => {
    const theme = useTheme();
    const [selectedSchools, setSelectedSchools] = useState<string[]>([]);
    const [matchAllSchools, setMatchAllSchools] = useState(false);

    const glassStyle = {
        background: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    };

    // Initialize with organization's school pre-selected (if it exists in the list)
    useEffect(() => {
        if (schools.length > 0 && organizationName) {
            // Find schools that match the organization name
            const matchingSchools = schools.filter((school) =>
                school.toLowerCase().includes(organizationName.toLowerCase().replace(/\s+/g, '')),
            );

            if (matchingSchools.length > 0) {
                setSelectedSchools(matchingSchools);
                setMatchAllSchools(false);
            } else {
                // Default to matching all schools if no org match
                setMatchAllSchools(true);
            }
        }
    }, [schools, organizationName]);

    const handleToggleMatchAll = () => {
        const newMatchAll = !matchAllSchools;
        setMatchAllSchools(newMatchAll);

        // If turning on match all, clear individual selections
        if (newMatchAll) {
            setSelectedSchools([]);
        }
    };

    const handleSchoolsChange = (event: any, newValue: string[]) => {
        setSelectedSchools(newValue);
        // If schools are selected, turn off "match all"
        if (newValue.length > 0) {
            setMatchAllSchools(false);
        }
    };

    const handleConfirm = () => {
        if (matchAllSchools) {
            onConfirm([]);
        } else {
            onConfirm(selectedSchools);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: glassStyle,
            }}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <SchoolIcon />
                    <Typography variant="h6">Select Schools to Match</Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Alert severity="info" sx={{ mb: 3 }}>
                    Choose which school(s) to match against your group members. Athletes from
                    unselected schools will appear as &quot;unmatched&quot; for manual review.
                </Alert>

                {organizationName && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Your organization: <strong>{organizationName}</strong>
                        </Typography>
                    </Box>
                )}

                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={`${schools.length} schools found`} color="primary" size="small" />
                    {!matchAllSchools && selectedSchools.length > 0 && (
                        <Chip
                            label={`${selectedSchools.length} selected`}
                            color="success"
                            size="small"
                        />
                    )}
                </Box>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={matchAllSchools}
                            onChange={handleToggleMatchAll}
                            color="primary"
                        />
                    }
                    label={
                        <Typography variant="body1" fontWeight="bold">
                            Match Against All Schools
                        </Typography>
                    }
                    sx={{ mb: 2 }}
                />

                {!matchAllSchools && (
                    <Box sx={{ ...glassStyle, p: 2 }}>
                        <Autocomplete
                            multiple
                            options={schools.sort()}
                            value={selectedSchools}
                            onChange={handleSchoolsChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Schools"
                                    placeholder="Type to search schools..."
                                    variant="outlined"
                                />
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                    const { key, ...tagProps } = getTagProps({ index });
                                    return (
                                        <Chip
                                            key={key}
                                            label={option}
                                            {...tagProps}
                                            color="primary"
                                            size="small"
                                        />
                                    );
                                })
                            }
                            ListboxProps={{
                                style: { maxHeight: 300 },
                            }}
                            sx={{ width: '100%' }}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onCancel} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="primary"
                    disabled={!matchAllSchools && selectedSchools.length === 0}
                >
                    Continue to Review Matches
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TrackEventSchoolSelector;
